import { useRef, useState } from 'react';
import { jobAPI } from '../../../services/api';
import { appendEstimateLines, diagnosisNote } from '../parts/diagnosisEstimate';

// Extracted verbatim from JobManagement.js during Task 8's "slim the
// orchestrator" step.
//
// Addition to the plan (reported): the plan named hooks for the data loaders
// (Task 4) and the invoice/status flows (Task 5), but never assigned a home to
// the diagnosis flow — while Task 8 Step 2 lists what the orchestrator should
// still contain, and these four functions are not on that list. They own
// `selectedJob`, `diagnosisResult` and `showDiagnosisModal`, which nothing
// outside this flow and the EnhancedDiagnosisModal render touches, so the
// state moves in with them.
//
// `isLoading` stays owned by the orchestrator — this hook receives
// #52: this hook no longer takes `setIsLoading`. Every operation here opens or
// runs inside the diagnosis overlay, and flipping the orchestrator's shared
// flag unmounted the table -- and with it the trigger focus must return to.
//
// `runDiagnosis` used to call `loadJobs()` directly; that is inverted into
// `onDiagnosisComplete` so this hook does not depend on `useJobs`, matching
// Task 5's treatment of `updateJobStatus`.
//
// Coupling note: `setShowJobDetails` and `setSelectedJobForActions` are
// orchestrator-owned modal state that two of these functions close the
// relevant surface with. They are threaded in as constructor arguments rather
// than duplicated, the same pattern used throughout Tasks 4 and 5.
//
// `openInvoiceModal`, `setEditableParts` and `setEditableInvoice` are owned by
// useInvoiceBuilder and threaded in the same way, so `createEstimate` can hand
// the diagnosis over to the invoice form — the shop's estimate — just as the
// mechanic agent's "Use this" does (JobsTab.handleUseEstimate).
export const useDiagnosisFlow = ({
  onDiagnosisComplete,
  setShowJobDetails,
  setSelectedJobForActions,
  openInvoiceModal,
  setEditableParts,
  setEditableInvoice
}) => {
  const [showDiagnosisModal, setShowDiagnosisModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [diagnosisResult, setDiagnosisResult] = useState(null);
  // #52: overlay-scoped loading, deliberately NOT the orchestrator's shared
  // `isLoading`. That flag is passed to DataTable as `loading`, so flipping it
  // swaps the table out for a loading state and unmounts the row button that
  // opened the overlay. Modal.js then has no live node to restore focus to on
  // close, and a keyboard user is dropped back to <body>. Anything that runs
  // while an overlay is open must use this instead.
  const [overlayLoading, setOverlayLoading] = useState(false);
  // A ref, not state: two clicks in the same tick must both see the first.
  const diagnosisInFlight = useRef(false);

  const viewExistingDiagnosis = async (job) => {
    try {
      setOverlayLoading(true);

      // Fetch the existing diagnosis results
      const diagnosticHistory = await jobAPI.getDiagnosticHistory(job.id);

      if (diagnosticHistory.history && diagnosticHistory.history.length > 0) {
        // Get the most recent diagnosis
        const latestDiagnosis = diagnosticHistory.history[0];

        // Set the job and diagnosis result
        setSelectedJob(job);
        setDiagnosisResult(latestDiagnosis);
        setShowDiagnosisModal(true);
        setShowJobDetails(false); // Close the details modal
      } else {
        alert('No existing diagnosis found for this job. Please run a new diagnosis.');
      }
    } catch (error) {
      console.error('Error fetching existing diagnosis:', error);
      alert('Error loading existing diagnosis results. Please try running a new diagnosis.');
    } finally {
      setOverlayLoading(false);
    }
  };

  const viewJobDiagnosis = async (job) => {
    try {
      console.log('Viewing diagnosis for job:', job.id);
      setOverlayLoading(true);

      // Fetch the existing diagnosis results
      const diagnosticHistory = await jobAPI.getDiagnosticHistory(job.id);

      if (diagnosticHistory.history && diagnosticHistory.history.length > 0) {
        // Get the most recent diagnosis
        const latestDiagnosis = diagnosticHistory.history[0];

        // Set the job and diagnosis result
        setSelectedJob(job);
        setDiagnosisResult(latestDiagnosis);
        setShowDiagnosisModal(true);
        setSelectedJobForActions(null); // Close the actions dropdown
      } else {
        alert('No diagnosis found for this job. Please run a diagnosis first.');
      }
    } catch (error) {
      console.error('Error viewing diagnosis:', error);
      alert(`Error viewing diagnosis: ${error.message || 'Unknown error'}`);
    } finally {
      setOverlayLoading(false);
    }
  };

  const runDiagnosis = async (job) => {
    // One at a time: each call is a billed Claude diagnosis that takes ~45s.
    if (diagnosisInFlight.current) return;
    diagnosisInFlight.current = true;
    try {
      setOverlayLoading(true);
      setSelectedJob(job);

      const diagnosis = await jobAPI.runDiagnosis(job.id, {
        symptoms: job.symptom_description
      });

      setDiagnosisResult(diagnosis);
      setShowDiagnosisModal(true);
      onDiagnosisComplete(); // Refresh jobs to show updated status
    } catch (error) {
      console.error('Error running diagnosis:', error);
      alert(`Error running diagnosis: ${error.message || 'Unknown error'}`);
    } finally {
      diagnosisInFlight.current = false;
      setOverlayLoading(false);
    }
  };

  // Opens the job's invoice form with `lines` appended after whatever it
  // already holds, labour hours from the diagnosis if it has none, and a note
  // saying where the lines came from. Nothing is saved: the mechanic reviews
  // the form and saves it, as with any invoice.
  const createEstimate = async (lines) => {
    const job = selectedJob;
    const diagnosis = diagnosisResult;
    setShowDiagnosisModal(false);
    await openInvoiceModal(job);
    setEditableParts((prev) => appendEstimateLines(prev, lines));
    setEditableInvoice((prev) => ({
      ...prev,
      labor_hours: prev.labor_hours || diagnosis?.estimated_labor_hours || 0,
      notes: [prev.notes, diagnosisNote(diagnosis)].filter(Boolean).join('\n\n')
    }));
  };

  return {
    showDiagnosisModal,
    setShowDiagnosisModal,
    selectedJob,
    setSelectedJob,
    diagnosisResult,
    viewExistingDiagnosis,
    viewJobDiagnosis,
    runDiagnosis,
    createEstimate,
    diagnosisOverlayLoading: overlayLoading
  };
};
